import { ParticipantTile, TrackReferenceOrPlaceholder } from '@livekit/components-react'
import { Room } from 'livekit-client'
import { useEffect, useState } from 'react'

import useMuseTracks, { MuseTracks } from './useMuseTracks'
import {
    aspectRatio,
    calculateOptimalVideoSize,
    chunkArrayByMaxSubarrayLength,
    fallbackContainerBounds,
} from '@/utlis/video-sizing'
import { unstable_batchedUpdates } from 'react-dom'
import { useResizeDetector } from 'react-resize-detector'

type Props = {
    room: Room
    galleryOrdering: string[] | null | undefined
    hiddenParticipantIds: string[]
}

const FuzzyGridLayout = ({ room, galleryOrdering, hiddenParticipantIds }: Props) => {
    const museTracks = useMuseTracks(room, '')

    const {
        ref,
        width: _containerWidth,
        height: _containerHeight,
    } = useResizeDetector({ refreshMode: 'debounce', refreshRate: 100 })
    const containerWidth = _containerWidth || fallbackContainerBounds.width
    const containerHeight = _containerHeight || fallbackContainerBounds.height

    return (
        <div ref={ref} id="video-gallery-container" style={{ width: '100vw', height: '100vh' }}>
            <VideoRows
                museTracks={museTracks}
                galleryOrdering={galleryOrdering}
                hiddenParticipantIds={hiddenParticipantIds}
                containerWidth={containerWidth}
                containerHeight={containerHeight}
            />
        </div>
    )
}

function VideoRows({
    museTracks,
    galleryOrdering,
    hiddenParticipantIds,
    containerWidth,
    containerHeight,
}: {
    museTracks: MuseTracks
    galleryOrdering: string[] | null | undefined
    hiddenParticipantIds: string[] | null | undefined
    containerWidth: number
    containerHeight: number
}) {
    const [rows, setRows] = useState<TrackReferenceOrPlaceholder[][]>([[]])

    const [videoWidth, setVideoWidth] = useState<number>(800)
    const [videoContainerHeight, setVideoContainerHeight] = useState(0)

    useEffect(() => {
        let vids: TrackReferenceOrPlaceholder[]
        if (galleryOrdering) {
            vids = []
            for (const clientSessionId of galleryOrdering) {
                const tracks = museTracks.all.filter((p) => p.participant.identity === clientSessionId)
                for (const track of tracks) {
                    vids.push(track)
                }
            }
        } else {
            vids = museTracks.all
        }
        vids = vids.filter((t) => {
            if (!hiddenParticipantIds) {
                return true
            }
            return !hiddenParticipantIds.includes(t.participant.identity)
        })

        const videoCt = vids.length

        const { colCt, fitWidth } = calculateOptimalVideoSize(videoCt, containerWidth, containerHeight)

        const safetyMargin = 10
        const videoWidth = fitWidth / colCt - safetyMargin
        // Chunk array based on the previously calculated optimal # of rows.
        const rows: TrackReferenceOrPlaceholder[][] = chunkArrayByMaxSubarrayLength(vids, colCt).filter(
            (x) => x.length > 0
        )

        unstable_batchedUpdates(() => {
            setVideoContainerHeight(videoContainerHeight)
            setVideoWidth(videoWidth)
            setRows(rows)
        })
    }, [containerWidth, containerHeight, museTracks, galleryOrdering])

    return (
        <>
            {rows.length > 0 && (
                <div className="h-full flex flex-col justify-center items-center gap-2">
                    {rows.map((items, rowIndex) => (
                        <Row
                            key={rowIndex}
                            rowIndex={rowIndex}
                            items={items}
                            videoWidth={videoWidth}
                            videoHeight={videoWidth / aspectRatio}
                        />
                    ))}
                </div>
            )}
        </>
    )
}

function Row({
    items,
    videoWidth,
    videoHeight,
}: {
    rowIndex: number
    items: TrackReferenceOrPlaceholder[]
    videoWidth: number
    videoHeight: number
}) {
    return (
        <div id="gallery-video-row" className="flex items-center gap-2">
            {items.map((item, colIndex) => {
                return (
                    <Video
                        key={item.publication?.trackSid}
                        item={item}
                        style={{ width: videoWidth, height: videoHeight, borderRadius: '10px' }}
                    />
                )
            })}
        </div>
    )
}

function Video({ item, style }: { item: TrackReferenceOrPlaceholder; style: any }) {
    return <ParticipantTile key={'PeerVideoWindow-' + item.publication?.trackSid} trackRef={item} style={style} />
}

export default FuzzyGridLayout
